import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getGradingScales,
  createGradingScale,
  updateGradingScale,
  deleteGradingScale,
  getGradingScaleById,
} from '../services/gradingScaleService';
import Notification from '../components/Common/Notification';

export const GradingScaleContext = createContext();

export const GradingScaleProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [gradingScales, setGradingScales] = useState([]);
  const [selectedGradingScale, setSelectedGradingScale] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchGradingScales = async (gradingPolicyId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradingScales(gradingPolicyId, authHeaders);
      setGradingScales(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching grading scales.';
      showNotification(errorMessage, true);
    }
  };

  const fetchGradingScaleById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradingScaleById(id, authHeaders);
      setSelectedGradingScale(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grading scale with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addGradingScale = async (gradingScaleData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newGradingScale = await createGradingScale(gradingScaleData, authHeaders);
      setGradingScales(prevScales => [...prevScales, newGradingScale]);
      showNotification('Grading scale added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding grading scale.';
      showNotification(errorMessage, true);
    }
  };

  const editGradingScale = async (id, gradingScaleData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedGradingScale = await updateGradingScale(id, gradingScaleData, authHeaders);
      setGradingScales(prevScales =>
        prevScales.map(scale => (scale.id === id ? updatedGradingScale : scale))
      );
      showNotification('Grading scale updated successfully!', false);
      return updatedGradingScale;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating grading scale.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeGradingScale = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteGradingScale(id, authHeaders);
      setGradingScales(prevScales => prevScales.filter(scale => scale.id !== id));
      showNotification('Grading scale removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing grading scale.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchGradingScales();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <GradingScaleContext.Provider
      value={{
        gradingScales,
        selectedGradingScale,
        fetchGradingScales,
        fetchGradingScaleById,
        addGradingScale,
        editGradingScale,
        removeGradingScale,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </GradingScaleContext.Provider>
  );
};

export const useGradingScale = () => useContext(GradingScaleContext);
